import { create } from 'zustand';
import { piApi, sprintApi } from '../services/api';
import type { ProgramIncrement, ProgramIncrementInput, Sprint } from '../types';

interface PIState {
  pis: ProgramIncrement[];
  sprints: Sprint[];
  loading: boolean;
  error: string | null;

  fetchPIs: () => Promise<void>;
  fetchAllSprints: () => Promise<void>;
  fetchSprintsForPI: (piId: string) => Promise<Sprint[]>;
  createPI: (data: ProgramIncrementInput) => Promise<ProgramIncrement>;
  updatePI: (id: string, data: ProgramIncrementInput) => Promise<ProgramIncrement>;
  deletePI: (id: string) => Promise<void>;
  updateSprint: (id: string, data: Partial<Sprint>) => Promise<Sprint>;
  getSprintsForPI: (piId: string) => Sprint[];
  startSprint: (sprintId: string) => Promise<{ sprint: Sprint; incompleteItems: any[] }>;
  rolloverItems: (sprintId: string, workItemIds: string[]) => Promise<number>;
}

export const usePIStore = create<PIState>((set, get) => ({
  pis: [],
  sprints: [],
  loading: false,
  error: null,

  fetchPIs: async () => {
    set({ loading: true, error: null });
    try {
      const pis = await piApi.list();
      set({ pis, loading: false });
    } catch (err: any) {
      set({ error: err.message, loading: false });
    }
  },

  fetchAllSprints: async () => {
    try {
      const sprints = await sprintApi.list();
      set({ sprints });
    } catch (err: any) {
      set({ error: err.message });
    }
  },

  fetchSprintsForPI: async (piId) => {
    try {
      const sprints = await piApi.getSprints(piId);
      // Merge into state (replace sprints for this PI, keep others)
      set((s) => ({
        sprints: [
          ...s.sprints.filter((sp) => sp.piId !== piId),
          ...sprints,
        ],
      }));
      return sprints;
    } catch (err: any) {
      set({ error: err.message });
      return [];
    }
  },

  createPI: async (data) => {
    const pi = await piApi.create(data);
    set((s) => ({ pis: [...s.pis, pi] }));
    // Fetch sprints for the new PI
    const sprints = await piApi.getSprints(pi._id);
    set((s) => ({ sprints: [...s.sprints, ...sprints] }));
    return pi;
  },

  updatePI: async (id, data) => {
    const pi = await piApi.update(id, data);
    set((s) => ({
      pis: s.pis.map((p) => (p._id === id ? { ...p, ...pi } : p)),
    }));
    // If timing changed, refresh sprints
    if ('startDate' in data || 'sprintCount' in data || 'sprintLengthWeeks' in data) {
      const sprints = await piApi.getSprints(id);
      set((s) => ({
        sprints: [...s.sprints.filter((sp) => sp.piId !== id), ...sprints],
      }));
    }
    return pi;
  },

  deletePI: async (id) => {
    await piApi.delete(id);
    set((s) => ({
      pis: s.pis.filter((p) => p._id !== id),
      sprints: s.sprints.filter((sp) => sp.piId !== id),
    }));
  },

  updateSprint: async (id, data) => {
    const sprint = await sprintApi.update(id, data);
    set((s) => ({
      sprints: s.sprints.map((sp) => (sp._id === id ? { ...sp, ...sprint } : sp)),
    }));
    return sprint;
  },

  getSprintsForPI: (piId) => {
    return get()
      .sprints.filter((s) => s.piId === piId)
      .sort((a, b) => a.sprintNumber - b.sprintNumber);
  },

  startSprint: async (sprintId) => {
    const result = await sprintApi.start(sprintId);
    // Refresh all sprints to get updated statuses (previous sprint completed, this one active)
    await get().fetchAllSprints();
    return result;
  },

  rolloverItems: async (sprintId, workItemIds) => {
    const result = await sprintApi.rollover(sprintId, workItemIds);
    return result.rolledOver;
  },
}));
